import { useEffect,useState } from 'react';

import { ReactComponent as VlbcCloudIcon } from '../../assets/icons/lbc-cloud-black-small.svg';
import { ReactComponent as PlugBlackIcon } from '../../assets/icons/plug-black.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as RoundQuestionBlackIcon } from '../../assets/icons/round-question-black.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import {
  fetchVlbcs,
  updateVlbcStatusRedux,
  removeVlbc,
} from '../../redux/slice/VlbcSlice';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import vlbcApi from '../../utils/api/vlbc';

import AddVlbc from './components/AddVlbc';
import EditVlbc from './components/EditVlbc';
import ListVlbc from './components/ListVlbc';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface AssignChargersCount {
  matchedChargingPoints: number;
  unMatchedChargingPoints: number;
  vlbcTotalCount: number;
}
interface HeaderSupportProperties {
  chargerAssignCounts: AssignChargersCount;
}
/**
 * Header support content
 */
function HeaderSupportContent(properties: HeaderSupportProperties) {
  const { chargerAssignCounts } = properties;

  const [isHovered, setIsHovered] = useState(false);

 

  return (
    <div className="header-support-content">
      <div className="header-group">
        <span
          className="load-balancer-container-hover"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          <RoundQuestionBlackIcon />
          {isHovered && (
            <div className="load-balancer-container">
              <div className="section">
                <h3 className="title">Virtual Load Balancer</h3>
                <p className="description">
                  A virtual load balancer in EV charging stations intelligently distributes power
                  across multiple chargers, ensuring optimal performance, preventing overloads, and
                  maximizing energy efficiency—all without the need for physical hardware upgrades.
                  It adapts in real time to charging demands, making EV infrastructure smarter and
                  more scalable.
                </p>
                <div className="section-border" />
              </div>

              <div className="section">
                <h4 className="subtitle">Algorithm</h4>

                <div className="algo-block">
                  <h4>Equal Share</h4>
                  <p>
                    The Equal Sharing algorithm in virtual load balancers ensures fair power
                    distribution by evenly splitting available energy across active EV chargers.
                    This prevents overload, promotes efficient charging, and guarantees every
                    vehicle gets a balanced share—perfect for peak times and limited grid capacity.
                  </p>
                </div>
              </div>
            </div>
          )}
        </span>
        <div className="content-wrap">
          <div className="content">
            <span>
              <VlbcCloudIcon />
            </span>
            <div>No of VLB</div>
            <div className="count">
              <span>{chargerAssignCounts.vlbcTotalCount}</span>
            </div>
          </div>
          <div className="content">
            <span>
              <PlugBlackIcon />
            </span>
            <div>No of Chargers Assigned</div>
            <div className="count">
              <span>{chargerAssignCounts.matchedChargingPoints}</span>
            </div>
          </div>
          <div className="content">
            <span>
              <PlugBlackIcon />
            </span>
            <div>No of Chargers Not Assigned</div>
            <div className="count">
              <span>{chargerAssignCounts.unMatchedChargingPoints}</span>
            </div>
          </div>
        
        </div>
      </div>
    </div>
  );
}

/**
 * Vlbc management page
 */
function VlbcManagement() {
  const dispatch = useAppDispatch();

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const [chargerAssignCounts, setChargerAssignCounts] = useState<AssignChargersCount>({
    matchedChargingPoints: 0,
    unMatchedChargingPoints: 0,
    vlbcTotalCount: 0,
  });

  /**
   * Close Add vlbc modal
   */
  const closeVlbcModal = () => {
    setModalBoxStatus('close');
  };

  /**
   * Fetch Chargers Assign Count
   */
  const fetchChargersAssignCount = async () => {
    vlbcApi
      .assignChargersCount()
      .then((response) => {
        const chargerCountInfo = response.data;
        setChargerAssignCounts(chargerCountInfo);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the openAddFacilityModal function
   */
  const openAddVlbcModal = () => {
    setModalBoxDetail({
      title: 'Add Load Balancer',
      content: (
        <AddVlbc
          handleCancel={() => setModalBoxStatus('close')}
          closeVlbcModal={closeVlbcModal}
          fetchChargersAssignCount={fetchChargersAssignCount}
        />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * handleEdit
   */
  const handleEdit = (vlbcId: string, event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    setModalBoxDetail({
      title: 'Edit Load Balancer',
      content: (
        <EditVlbc
          handleCancel={() => setModalBoxStatus('close')}
          closeVlbcModal={closeVlbcModal}
          vlbcId={vlbcId}
          fetchChargersAssignCount={fetchChargersAssignCount}
        />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * handleDelete
   */
  const handleDelete = (vlbcId: string) => {
    vlbcApi
      .deleteVlbc(vlbcId)
      .then(() => {
        dispatch(
          removeVlbc({
            vlbcId,
          }),
        );
        dispatch(
          setNotificationDetails({
            type: 'success',
            notificationDetails: {
              statusCode: 200,
              message: 'Deleted Successfully',
            },
          }),
        );

        dispatch(
          fetchVlbcs({
            page: DefaultApiLimit.page,
            limit: DefaultApiLimit.limit,
          }),
        );
        fetchChargersAssignCount();
      })
      .catch(() => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: 500,
              message: 'Something went wrong',
            },
          }),
        );
      });
  };

  /**
   * handle update isActive
   */
  const handleUpdateStatus = (vlbcId: string, isActive: boolean) => {
    vlbcApi
      .updateVlbcStatus(vlbcId, {
        isActive: !isActive,
      })
      .then(() => {
        dispatch(
          updateVlbcStatusRedux({
            vlbcId,
            isActive: !isActive,
          }),
        );
      })
      .catch(() => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: 500,
              message: 'Something went wrong',
            },
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(fetchVlbcs({}));
    fetchChargersAssignCount();
  }, []);

  return (
    <div className="vlbc-management-wrap">
      <ModalBox
        fullHeightWidth
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="vlbc-management-header">
        <PageTitle
          title="Virtual Load Balancer"
          buttonLabel="Create Load Balancer"
          buttonSvgIcon={<PlusIcon />}
          headerSupportContent={<HeaderSupportContent chargerAssignCounts={chargerAssignCounts} />}
          buttonType="submit"
          onClickButton={openAddVlbcModal}
        />
      </div>
      <br />
      <ListVlbc
        handleUpdateStatus={(id, isActive) => handleUpdateStatus(id, isActive)}
        handleEdit={(id, event) => handleEdit(id, event)}
        handleDelete={(id) => handleDelete(id)}
      />
    </div>
  );
}

export default VlbcManagement;
